/**
 * Promise executor utility for managing async promises in parallel.
 *
 * Provides two execution strategies:
 * - Critical: Must all succeed (Promise.all) - app cannot function without these
 * - Degradable: Can fail gracefully (Promise.allSettled) - app continues if these fail
 *
 * Timeout functionality:
 * - withTimeout: Wraps any promise with a timeout that rejects if exceeded
 * - Degradable promises: Always have timeout (default 90s, customizable per promise)
 * - Critical promises: Timeout disabled by default, enable with { enableTimeout: true }
 *
 * Monitoring: See promiseExecutorMonitoring.ts for tracing configuration
 */

import { interactionTracker } from "./plugin";
import {
  notifyPromiseComplete,
  buildMetrics,
  buildIndividualMetrics,
  generateTraceId,
} from "./promiseExecutorMonitoring";

/** Default timeout for promises (90 seconds) */
export const DEFAULT_PROMISE_TIMEOUT = 90000;

type PromiseType = "critical" | "degradable";

const getLogPrefix = (type: PromiseType, context: string): string =>
  `[${
    type === "critical" ? "CriticalPromise" : "DegradablePromise"
  }:${context}]`;

export interface MonitoringOptions {
  /** Context for monitoring (e.g., 'SideBar.onMounted') */
  context: string;
  /** Promise type for monitoring */
  type: "critical" | "degradable";
  /** Promise name for logging */
  promiseName: string;
  /** Trace ID for correlating promises within a batch */
  traceId?: string;
}

/**
 * Wraps a promise with monitoring to track individual promise timing.
 * Logs success/error metrics when the promise settles.
 */
const promiseWithMonitoring = <T>(
  promise: Promise<T>,
  options: MonitoringOptions
): Promise<T> => {
  const startTime = performance.now();
  const { context, type, promiseName, traceId } = options;

  return promise
    .then((result) => {
      notifyPromiseComplete(
        buildIndividualMetrics(
          context,
          type,
          promiseName,
          startTime,
          "success",
          undefined,
          traceId
        )
      );
      return result;
    })
    .catch((error) => {
      notifyPromiseComplete(
        buildIndividualMetrics(
          context,
          type,
          promiseName,
          startTime,
          "error",
          error,
          traceId
        )
      );
      throw error;
    });
};

/**
 * Wraps a promise with a timeout. Rejects if timeout expires before promise resolves.
 *
 * @param promise - The promise to wrap with timeout
 * @param timeoutMs - Timeout in milliseconds
 * @param promiseName - Name of the promise (for error messages)
 * @returns Promise that rejects if timeout expires
 *
 * @example
 * await withTimeout(fetchData(), 5000, 'fetchData')
 */
export const promiseWithTimeout = <T>(
  promise: Promise<T>,
  timeoutMs: number,
  promiseName?: string
): Promise<T> => {
  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(() => {
      reject(
        new Error(
          `[Timeout] ${promiseName || "Promise"} exceeded ${timeoutMs}ms`
        )
      );
    }, timeoutMs);
  });

  return Promise.race([promise, timeoutPromise]);
};

/**
 * Wraps a promise with both timeout and monitoring.
 *
 * @param promise - The promise to wrap
 * @param timeoutMs - Timeout in milliseconds
 * @param monitoringOptions - Context, type, and promise name for monitoring
 * @returns Promise with timeout and monitoring applied
 *
 * @example
 * await promiseWithTimeoutAndMonitoring(
 *   fetchData(),
 *   5000,
 *   { context: 'SideBar', type: 'degradable', promiseName: 'fetchData' }
 * )
 */
export const promiseWithTimeoutAndMonitoring = <T>(
  promise: Promise<T>,
  timeoutMs: number,
  monitoringOptions: MonitoringOptions
): Promise<T> => {
  const timedPromise = promiseWithTimeout(
    promise,
    timeoutMs,
    monitoringOptions.promiseName
  );
  return promiseWithMonitoring(timedPromise, monitoringOptions);
};

/**
 * Configuration for a named async promise with optional timeout
 */
export interface NamedPromise<T = unknown> {
  /** Unique name for logging/debugging */
  name: string;
  /** The promise to execute */
  promise: Promise<T>;
  /** Optional timeout in ms (defaults to DEFAULT_PROMISE_TIMEOUT) */
  timeout?: number;
}

/**
 * Configuration for critical promises
 */
export interface CriticalPromiseConfig {
  /** Context name for logging (e.g., 'SideBar.onMounted', 'RouterHelper.dispatchSyncAlls') - REQUIRED */
  context: string;
  /** Timeout in ms for all promises (defaults to DEFAULT_PROMISE_TIMEOUT) */
  timeout?: number;
  /** Enable timeout for critical promises (default: false - no timeout) */
  enableTimeout?: boolean;
}

const useNewTracking = Math.random() < 0.2;

/**
 * Executes multiple critical promises in parallel.
 * Uses Promise.all - fails fast if any promise fails.
 * These are promises the app cannot function without.
 *
 * @param promises - Array of critical promises (must all succeed)
 * @param config - Configuration with required context name
 * @returns Promise that resolves when all promises complete, or rejects on first failure
 *
 * @example
 * // Basic usage with context name
 * await executeCriticalPromises([
 *   { name: 'users/syncAll', promise: store.dispatch('users/syncAll', id) },
 *   { name: 'locations/reset', promise: store.dispatch('locations/resetCurrentLocation', { locationId }) },
 * ], { context: 'SideBar.onMounted' })
 *
 * @example
 * // With timeout enabled
 * await executeCriticalPromises(
 *   [{ name: 'users/syncAll', promise: store.dispatch('users/syncAll', id) }],
 *   { context: 'RouterHelper', enableTimeout: true, timeout: 10000 }
 * )
 */
export const executeCriticalPromises = async <T = unknown>(
  promises: NamedPromise<T>[],
  config: CriticalPromiseConfig
): Promise<T[]> => {
  const {
    context,
    timeout = DEFAULT_PROMISE_TIMEOUT,
    enableTimeout = false,
  } = config;
  if (useNewTracking) {
    try {
      const results = await interactionTracker?.trackInteraction(
        context,
        async () => {
          const timedPromises = promises.map((p) =>
            interactionTracker?.trackOperation(context, p.name, () =>
              enableTimeout
                ? promiseWithTimeout(p.promise, p.timeout ?? timeout, p.name)
                : p.promise
            )
          );
          return await Promise.all(timedPromises);
        }
      );
      return (results ?? []) as T[];
    } catch (error) {
      console.error(
        `${getLogPrefix("critical", context)} Failed. Promises: ${promises
          .map((p) => p.name)
          .join(", ")}`,
        error
      );
      throw error;
    }
  } else {
    const startTime = performance.now();
    const promiseNames = promises.map((p) => p.name);
    const traceId = generateTraceId();

    // Apply timeout and monitoring to each promise
    const timedPromises = promises.map((p) => {
      if (enableTimeout) {
        return promiseWithTimeoutAndMonitoring(
          p.promise,
          p.timeout ?? timeout,
          {
            context,
            type: "critical",
            promiseName: p.name,
            traceId,
          }
        );
      }
      return p.promise;
    });

    try {
      const results = await Promise.all(timedPromises);
      notifyPromiseComplete(
        buildMetrics(
          context,
          "critical",
          promiseNames,
          startTime,
          "success",
          undefined,
          undefined,
          traceId
        )
      );
      return results;
    } catch (error) {
      console.error(
        `${getLogPrefix(
          "critical",
          context
        )} Failed. Promises: ${promiseNames.join(", ")}`,
        error
      );
      notifyPromiseComplete(
        buildMetrics(
          context,
          "critical",
          promiseNames,
          startTime,
          "error",
          undefined,
          error as Error,
          traceId
        )
      );
      throw error;
    }
  }
};

/**
 * Configuration for degradable promises
 */
export interface DegradablePromiseConfig {
  /** Context name for logging (e.g., 'SideBar.onMounted', 'RouterHelper.dispatchSyncAlls') - REQUIRED */
  context: string;
  /** Callback for failed promises */
  onError?: (name: string, error: unknown) => void;
}

/**
 * Executes multiple degradable promises with individual timeouts.
 * Uses Promise.allSettled so one failure doesn't affect others.
 * Failures are logged but don't break the app - features degrade gracefully.
 *
 * @param promises - Array of promises to execute (can fail without breaking app)
 * @param config - Configuration with required context name
 *
 * @example
 * // With context name
 * await executeDegradablePromises([
 *   { name: 'featureFlags/syncAll', promise: store.dispatch('featureFlags/syncAll', id) },
 *   { name: 'customObjects/syncAll', promise: store.dispatch('customObjects/syncAll', id), timeout: 8000 },
 * ], { context: 'SideBar.onMounted' })
 *
 * @example
 * // With onError callback
 * await executeDegradablePromises(promises, {
 *   context: 'RouterHelper',
 *   onError: (name, error) => console.error(`${name} failed:`, error)
 * })
 */
export const executeDegradablePromises = async (
  promises: NamedPromise[],
  config: DegradablePromiseConfig
): Promise<PromiseSettledResult<unknown>[]> => {
  const { context, onError } = config;
  if (useNewTracking) {
    const results = await interactionTracker?.trackInteraction(
      context,
      async () => {
        const timedPromises = promises.map((p) =>
          interactionTracker?.trackOperation(context, p.name, () =>
            promiseWithTimeout(
              p.promise,
              p.timeout ?? DEFAULT_PROMISE_TIMEOUT,
              p.name
            )
          )
        );
        return await Promise.allSettled(timedPromises);
      }
    );

    results?.forEach((result, index) => {
      if (result.status === "rejected") {
        const promiseName = promises[index].name;
        if (onError) {
          onError(promiseName, result.reason);
        } else {
          console.error(
            `${getLogPrefix("degradable", context)} ${promiseName} failed:`,
            result.reason
          );
        }
      }
    });

    return results ?? [];
  } else {
    const startTime = performance.now();
    const promiseNames = promises.map((p) => p.name);
    const traceId = generateTraceId();

    // Apply timeout and monitoring to each promise
    const timedPromises = promises.map((p) =>
      promiseWithTimeoutAndMonitoring(
        p.promise,
        p.timeout ?? DEFAULT_PROMISE_TIMEOUT,
        {
          context,
          type: "degradable",
          promiseName: p.name,
          traceId,
        }
      )
    );

    const results = await Promise.allSettled(timedPromises);

    // Collect failed promises and handle errors
    const failedPromises = results.reduce<string[]>((failed, result, index) => {
      if (result.status === "rejected") {
        const promiseName = promises[index].name;
        failed.push(promiseName);
        if (onError) {
          onError(promiseName, result.reason);
        } else {
          console.error(
            `${getLogPrefix("degradable", context)} ${promiseName} failed:`,
            result.reason
          );
        }
      }
      return failed;
    }, []);

    // Notify monitoring
    const status = failedPromises.length > 0 ? "error" : "success";
    notifyPromiseComplete(
      buildMetrics(
        context,
        "degradable",
        promiseNames,
        startTime,
        status,
        failedPromises.length > 0 ? failedPromises : undefined,
        undefined,
        traceId
      )
    );

    return results;
  }
};
